// First-party visitor beacon (IDD D-HP-CADDY-011, D-SAAS-176). Nothing leaves the browser before
// the visitor has decided. "Statistics" sends page, dwell, scroll and click events without any
// identifier. Accepting everything adds the random visitor id, the session, the browser's
// characteristics, a heartbeat every 30 seconds while the page is visible and the page's dwell,
// scroll depth and interaction count. Pages under /admin send nothing and mark the browser internal.
import { PRIVACY_VERSION, CONSENT_STORAGE_KEY } from '../config/consent.js';

const VID_KEY = 'paioneer_vid';
const SID_KEY = 'paioneer_sid';
const UTM_KEY = 'paioneer_utm';
const INTERNAL_KEY = 'paioneer_internal';
const SESSION_IDLE_MS = 30 * 60 * 1000;
const HEARTBEAT_MS = 30 * 1000;
const MAX_HEARTBEATS_PER_PAGE = 120;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const randomId = (prefix) => {
  const bytes = new Uint8Array(12);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return `${prefix}-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
};

const storageGet = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const storageSet = (store, key, value) => {
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    // Storage may be blocked; the beacon then simply carries less.
  }
};

export const readConsent = () => {
  try {
    const stored = JSON.parse(storageGet(localStorage, CONSENT_STORAGE_KEY) || 'null');
    return stored && stored.version === PRIVACY_VERSION ? stored : null;
  } catch {
    return null;
  }
};

export const consentMode = (consent) => {
  if (!consent) return 'undecided';
  if (consent.statistics && consent.marketing) return 'profile';
  return consent.statistics ? 'statistics' : 'none';
};

const isAdminPath = () => window.location.pathname.includes('/admin');

const send = (payload) => {
  try {
    if (typeof window === 'undefined' || isAdminPath()) return;
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon && navigator.sendBeacon('/api/collect', new Blob([body], { type: 'application/json' }))) return;
    fetch('/api/collect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    // Telemetry never interrupts the page.
  }
};

const visitorId = (create) => {
  let vid = storageGet(localStorage, VID_KEY);
  if (!vid && create) {
    vid = randomId('v');
    storageSet(localStorage, VID_KEY, vid);
  }
  return vid || '';
};

const sessionId = () => {
  const now = Date.now();
  try {
    const stored = JSON.parse(storageGet(sessionStorage, SID_KEY) || 'null');
    if (stored && now - stored.at < SESSION_IDLE_MS) {
      storageSet(sessionStorage, SID_KEY, JSON.stringify({ id: stored.id, at: now }));
      return stored.id;
    }
  } catch {
    // A broken entry starts a new session.
  }
  const id = randomId('s');
  storageSet(sessionStorage, SID_KEY, JSON.stringify({ id, at: now }));
  return id;
};

// Stores the decision, records its proof (random consent id, choice, statement version, no
// address) and, when the choice falls below "everything", lets the server delete the profile.
export const storeConsent = (choice, lang) => {
  const previous = readConsent();
  const marketing = !!choice.marketing;
  const statistics = marketing || !!choice.statistics;
  const next = {
    version: PRIVACY_VERSION,
    statistics,
    marketing,
    consentId: previous?.consentId || randomId('c'),
    decidedAt: new Date().toISOString(),
  };
  storageSet(localStorage, CONSENT_STORAGE_KEY, JSON.stringify(next));
  const mode = consentMode(next);
  const formerVid = visitorId(false);
  if (mode !== 'profile') {
    storageSet(localStorage, VID_KEY, null);
    storageSet(sessionStorage, SID_KEY, null);
  }
  if (mode === 'none') storageSet(sessionStorage, UTM_KEY, null);
  send({
    event_type: 'consent',
    mode,
    consent_id: next.consentId,
    version: PRIVACY_VERSION,
    lang,
    vid: mode === 'profile' ? visitorId(true) : formerVid,
  });
  return next;
};

export const markInternal = () => storageSet(localStorage, INTERNAL_KEY, '1');

const readUtmParams = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const current = {};
    UTM_KEYS.forEach((k) => {
      const val = params.get(k);
      if (val) current[k] = val.slice(0, 120);
    });
    if (Object.keys(current).length) {
      storageSet(sessionStorage, UTM_KEY, JSON.stringify(current));
      return current;
    }
    return JSON.parse(storageGet(sessionStorage, UTM_KEY) || '{}');
  } catch {
    return {};
  }
};

let cachedSignals = null;

// The browser characteristics the privacy statement lists, read once per page load.
const collectSignals = () => {
  if (cachedSignals) return cachedSignals;
  const n = navigator || {};
  let gl = '';
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (ctx) {
      const info = ctx.getExtension('WEBGL_debug_renderer_info');
      gl = String(ctx.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : ctx.RENDERER) || '');
      const lose = ctx.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    }
  } catch {
    gl = '';
  }
  const ua = n.userAgent || '';
  const brands = ((n.userAgentData && n.userAgentData.brands) || []).map((b) => b.brand).join(' ');
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    tz = '';
  }
  cachedSignals = {
    webdriver: !!n.webdriver,
    headless: /HeadlessChrome/i.test(ua) || /HeadlessChrome/i.test(brands),
    plugins: n.plugins ? n.plugins.length : 0,
    langs: Array.isArray(n.languages) ? n.languages.slice(0, 8) : [],
    tz,
    scr: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    win: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    outer: `${window.outerWidth || 0}x${window.outerHeight || 0}`,
    dpr: window.devicePixelRatio || 1,
    cores: n.hardwareConcurrency || 0,
    mem: n.deviceMemory || 0,
    touch: n.maxTouchPoints || 0,
    gl,
    gpc: !!n.globalPrivacyControl,
    dnt: n.doNotTrack === '1' || window.doNotTrack === '1',
    platform: (n.userAgentData && n.userAgentData.platform) || n.platform || '',
    chrome: typeof window.chrome !== 'undefined',
    cookies: !!n.cookieEnabled,
  };
  return cachedSignals;
};

// The page being read: its visible time, deepest scroll and interaction count.
let page = null;
let started = false;
let lastMoveAt = 0;

const visibleSeconds = () => {
  if (!page) return 0;
  const running = document.visibilityState === 'visible' && page.visibleSince ? Date.now() - page.visibleSince : 0;
  return Math.round((page.visibleMs + running) / 100) / 10;
};

const scrollDepth = () => {
  const doc = document.documentElement;
  const room = Math.max(0, (doc.scrollHeight || 0) - (window.innerHeight || 0));
  if (room <= 0) return 100;
  return Math.min(100, Math.round((100 * (window.scrollY || doc.scrollTop || 0)) / room));
};

const pageBase = () => {
  const consent = readConsent();
  const mode = consentMode(consent);
  if (!page || (mode !== 'statistics' && mode !== 'profile')) return null;
  const base = { mode, path: page.path, lang: page.lang, internal: storageGet(localStorage, INTERNAL_KEY) === '1' };
  if (mode === 'profile') {
    Object.assign(base, {
      vid: visitorId(true),
      sid: sessionId(),
      pid: page.pid,
      consent_id: consent.consentId,
      version: PRIVACY_VERSION,
    });
  }
  return base;
};

const reportPage = (eventType) => {
  const base = pageBase();
  if (!base) return;
  if (base.mode === 'statistics') {
    if (eventType !== 'page_end' || page.ended) return;
    page.ended = true;
  }
  send({
    ...base,
    event_type: eventType,
    dwell_seconds: visibleSeconds(),
    scroll: page.scrollMax,
    ...(base.mode === 'profile' ? { interactions: page.interactions } : {}),
  });
};

const countInteraction = (event) => {
  if (!page) return;
  if (event.type === 'pointermove') {
    const now = Date.now();
    if (now - lastMoveAt < 250) return;
    lastMoveAt = now;
  }
  page.interactions += 1;
};

const startTracking = () => {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('scroll', () => {
    if (page) page.scrollMax = Math.max(page.scrollMax, scrollDepth());
  }, { passive: true });
  ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => {
    window.addEventListener(type, countInteraction, { passive: true, capture: true });
  });
  document.addEventListener('visibilitychange', () => {
    if (!page) return;
    if (document.visibilityState === 'hidden') {
      if (page.visibleSince) page.visibleMs += Date.now() - page.visibleSince;
      page.visibleSince = 0;
      reportPage('page_end');
    } else {
      page.visibleSince = Date.now();
    }
  });
  window.addEventListener('pagehide', () => reportPage('page_end'));
  window.setInterval(() => {
    if (!page || document.visibilityState !== 'visible' || page.heartbeats >= MAX_HEARTBEATS_PER_PAGE) return;
    if (consentMode(readConsent()) !== 'profile') return;
    page.heartbeats += 1;
    reportPage('heartbeat');
  }, HEARTBEAT_MS);
};

export const trackPageview = ({ path, search, lang, title }) => {
  startTracking();
  if (page) reportPage('page_end');
  const mode = consentMode(readConsent());
  if (mode !== 'statistics' && mode !== 'profile') {
    page = null;
    return;
  }
  page = {
    pid: randomId('p'),
    path: `${path}${search || ''}`,
    lang,
    visibleMs: 0,
    visibleSince: document.visibilityState === 'visible' ? Date.now() : 0,
    scrollMax: scrollDepth(),
    interactions: 0,
    heartbeats: 0,
    ended: false,
  };
  const base = pageBase();
  send({
    ...base,
    event_type: 'pageview',
    title,
    referrer: document.referrer || '',
    screen: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    ...readUtmParams(search),
    ...(base.mode === 'profile' ? { signals: collectSignals() } : {}),
  });
};

export const trackClick = (target) => {
  const base = pageBase();
  if (!base) return;
  send({ ...base, event_type: 'cta_click', path: window.location.pathname, cta_target: target, ...readUtmParams(window.location.search) });
};
